/**
 * axios封装
 * 请求拦截、响应拦截、错误统一处理
 */
import axios from 'axios'
import type { AxiosRequestConfig, AxiosResponse, AxiosInstance, CancelTokenSource } from 'axios'
import { router } from '@/router'
import { useAppStore } from '@/store/app'
import { getToken, setToken } from '@/utils/token'
import settings from '@/settings'
import { hideLoading, showLoading } from './loading'
import { ContentTypeEnum, STATUS_CODE_MESSAGE } from '@shared/enums/httpEnum'
import { RouterEnum } from '@/enums/routerEnum'
import type { RouteLocationRaw } from 'vue-router'

const { TokenKeyOfHeader, timeout } = settings
const cancelToken = axios.CancelToken
let source: CancelTokenSource = cancelToken.source()

/**
 * 提示函数
 * 禁止点击蒙层、显示5秒后关闭
 */
const tip = (msg: string) => {
	ElMessage({
		message: msg,
		duration: 4000,
		showClose: true,
		type: 'error',
	})
}

const startLoading = () => {
	showLoading()
}
const closeLoading = () => {
	hideLoading()
}

/**
 * 跳转登录页
 */
const toLogin = async () => {
	const appStore = useAppStore()
	await appStore.logout()
	const redirect = router.currentRoute.value.fullPath
	const route: RouteLocationRaw = { path: RouterEnum.LOGIN_PAGE_PATH }
	if (![RouterEnum.LOGIN_PAGE_PATH, '/'].includes(redirect)) route.query = { redirect }
	router.push(route)
}

// 创建axios实例
const instance: AxiosInstance = axios.create({ timeout })
// 设置post请求头、设置put请求头
instance.defaults.headers.post['Content-Type'] = instance.defaults.headers.put['Content-Type'] = ContentTypeEnum.JSON
/**
 * 请求拦截器
 * 每次请求前，如果存在token则在请求头中携带token
 */

instance.interceptors.request.use(
	(config: AxiosRequestConfig) => {
		// 登录流程控制中，根据本地是否存在token判断用户的登录情况
		// 但是即使token存在，也有可能token是过期的，所以在每次的请求头中携带token
		// 后台根据携带的token判断用户的登录情况，并返回给我们对应的状态码
		// 而后我们可以在响应拦截器中，根据状态码进行一些统一的操作。
		const token = getToken()
		token && config.headers && (config.headers[TokenKeyOfHeader] = token)
		config.cancelToken = source.token
		if (!config.hideLoading) {
			startLoading()
		}
		return config
	},
	error => Promise.reject(error),
)

// 响应拦截器
instance.interceptors.response.use(
	// 请求成功
	(res: AxiosResponse) => {
		if (!res.config.hideLoading) {
			closeLoading()
		}
		if (res.status === 200) {
			return successHandle(res)
		} else {
			return Promise.reject(res)
		}
	},
	// 请求失败
	error => {
		closeLoading()
		const { response } = error
		if (response) {
			// 请求已发出，但是不在2xx的范围
			errorHandle(response.status, response.data)
			return Promise.reject(response)
		} else {
			if (!window.navigator.onLine) {
				// 处理断网的情况
			} else {
				return Promise.reject(error)
			}
		}
	},
)

/**
 * 请求200成功后的统一处理
 * @param {Object} res 响应数据
 */
const successHandle = (res: AxiosResponse) => {
	const { data, msg, code } = res.data as ResType
	const newToken = res.headers[TokenKeyOfHeader.toLowerCase()]
	if (newToken) {
		setToken(newToken)
		const appStore = useAppStore()
		appStore.token = newToken
	}
	if (code === undefined) {
		// 文件的情况
		return Promise.resolve(res)
	}
	if (code === 0) {
		// 成功响应，成功返回
		return Promise.resolve(data)
	} else {
		// 后台成功响应，但是存在问题
		tip(msg ?? '')
		return Promise.reject(msg)
	}
}

/**
 * 请求失败后的错误统一处理
 * @param {Number} status 请求失败的状态码
 */
const errorHandle = (status: keyof typeof STATUS_CODE_MESSAGE, data: ResType): void => {
	tip(data?.msg ?? STATUS_CODE_MESSAGE[status] ?? '未知错误')
	if ([401, 403, 502, 503].includes(status)) {
		//取消后续请求
		source.cancel()
		source = cancelToken.source()
		setTimeout(() => {
			// 退出登录，跳转到登录页
			toLogin()
		}, 1000)
	}
}
export default instance
